import algoliasearch, { SearchClient, SearchIndex } from 'algoliasearch';
import type { WatchReferenceAlgoliaHit, AlgoliaSearchResponse } from '../types';
import {
  MAX_SIBLING_REFERENCES,
  MIN_SIBLING_REFERENCES,
  siblingReferencesFromHits,
  type IndexedSiblingReferences,
  type SiblingReferenceHit,
  type SiblingSubject,
} from './siblingReferences';
import { collectionQueryVariants } from './notableVariantCandidates';
import {
  BRAND_LISTING_FACETS,
  REFERENCE_SORT_REPLICAS,
  buildReferenceListParams,
  foldFacetValues,
  foldForMatch,
  type BrandFacetValue,
  type ReferenceListFilters,
  type ReferenceSortKey,
} from './referenceListQuery';
import {
  buildBrandSearchParams,
  type BrandSearchFacetName,
  type BrandSearchFilters,
} from './brandSearchQuery';
import { buildBrandListParams, type BrandListingHit } from './brandListQuery';
import {
  buildManufacturerCatalogueParams,
  manufacturerHitToEntry,
  sortManufacturersByName,
  type ManufacturerCatalogueEntry,
  type ManufacturerCatalogueHit,
} from './manufacturerCatalogueQuery';
import {
  buildCalibreSearchParams,
  type CalibreSearchFilters,
} from './calibreSearchQuery';
import {
  buildWinderCatalogueParams,
  sortWindersByModel,
  winderHitToEntry,
  type WinderCatalogueEntry,
  type WinderCatalogueHit,
} from './winderCatalogueQuery';
import {
  CALIBRE_PAGINATION_LIMIT,
  CALIBRE_PARTITION_DRIFT_TOLERANCE,
  CALIBRE_PARTITION_FACETS,
  buildCalibreListParams,
  type CalibreListingHit,
} from './calibreListQuery';

export {
  BRAND_LIST_ATTRIBUTES,
  BRAND_FIELDS_NOT_INDEXED,
  CEASED_BRAND_STATUSES,
  brandHitToWatchBrand,
  buildBrandListParams,
  mergeBrandDocs,
} from './brandListQuery';
export type { BrandListingHit } from './brandListQuery';

export {
  MANUFACTURER_CATALOGUE_ATTRIBUTES,
  MANUFACTURER_FIELDS_NOT_INDEXED,
  buildManufacturerCatalogueParams,
  manufacturerHitToEntry,
  sortManufacturersByName,
} from './manufacturerCatalogueQuery';
export type {
  ManufacturerCatalogueEntry,
  ManufacturerCatalogueHit,
} from './manufacturerCatalogueQuery';

export {
  WINDER_CATALOGUE_ATTRIBUTES,
  buildWinderCatalogueParams,
  winderHitToEntry,
  sortWindersByModel,
} from './winderCatalogueQuery';
export type {
  WinderCatalogueEntry,
  WinderCatalogueHit,
} from './winderCatalogueQuery';

export {
  BRAND_SEARCH_ATTRIBUTES,
  BRAND_SEARCH_FACETS,
  localBrandMatches,
  mergeBrandSearchResults,
} from './brandSearchQuery';
export type {
  BrandSearchable,
  BrandSearchFacetName,
  BrandSearchFilters,
} from './brandSearchQuery';

export {
  CALIBRE_SEARCH_ATTRIBUTES,
  CALIBRE_FIELDS_NOT_INDEXED,
  buildCalibreSearchParams,
  localCalibreMatches,
  mergeCalibreSearchResults,
  orderByHitIds,
} from './calibreSearchQuery';
export type {
  CalibreSearchable,
  CalibreSearchFilters,
} from './calibreSearchQuery';

export {
  CALIBRE_LIST_ATTRIBUTES,
  CALIBRE_LIST_FIELDS_NOT_INDEXED,
  CALIBRE_PAGINATION_LIMIT,
  CALIBRE_PARTITION_DRIFT_TOLERANCE,
  CALIBRE_PARTITION_FACETS,
  buildCalibreListParams,
  calibreDocToRow,
  calibreHitToRow,
  calibreRowBrandKey,
} from './calibreListQuery';
export type { CalibreListingHit, CalibreListRow } from './calibreListQuery';

export {
  BRAND_LISTING_FACETS,
  REFERENCE_LIST_ATTRIBUTES,
  REFERENCE_SORT_REPLICAS,
  buildReferenceListParams,
  isServerSortable,
} from './referenceListQuery';
export type {
  BrandFacetValue,
  BrandListingFacetName,
  ReferenceListFilters,
  ReferenceSortKey,
} from './referenceListQuery';

// Initialize Algolia client
const appId = import.meta.env.VITE_ALGOLIA_APP_ID || '';
const searchKey = import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY || '';
const indexName =
  import.meta.env.VITE_ALGOLIA_WATCH_REFERENCES_INDEX || 'watch_references';

let client: SearchClient | null = null;
let index: SearchIndex | null = null;

function getClient(): SearchClient {
  if (!client) {
    if (!appId || !searchKey) {
      throw new Error('Algolia credentials not configured');
    }
    client = algoliasearch(appId, searchKey);
  }
  return client;
}

function getIndex(): SearchIndex {
  if (!index) {
    index = getClient().initIndex(indexName);
  }
  return index;
}

export function isAlgoliaConfigured(): boolean {
  return !!appId && !!searchKey;
}

// Entity index names are stable and managed server-side (push-settings /
// backfill callables hardcode the same names), so we don't gate them behind
// env vars the way the references index is.
const CALIBRES_INDEX = 'watchkeeper_calibres';
const CALIBRE_FAMILIES_INDEX = 'watchkeeper_calibre_families';
const ELECTRONIC_MODULES_INDEX = 'watchkeeper_electronic_modules';
const BRANDS_INDEX = 'watchkeeper_brands';
/**
 * NOT `watchkeeper_movement_manufacturers` — the index is named for the entity,
 * not for the `movement_manufacturers` collection that feeds it. See
 * `ALGOLIA_INDEXES.manufacturers` in watchlock `functions/src/algoliaConfig.ts`.
 */
const MANUFACTURERS_INDEX = 'watchkeeper_manufacturers';
const WINDERS_INDEX = 'watchkeeper_winders';

/**
 * How many brands and manufacturers carry each raw `parentOrg` string.
 *
 * `parentOrg` is a facet on both entity indexes (verified on the live ones), so
 * this is two facet-only requests and no Firestore reads. The keys are the RAW
 * stored strings — mixed `Richemont` / `rolex_sa` spellings — because brand and
 * manufacturer docs still carry the free-text field until parent-org Phase 3;
 * `countParentOrgUsage` resolves them to an organization.
 */
export async function getParentOrgUsageCounts(): Promise<{
  brands: Record<string, number>;
  manufacturers: Record<string, number>;
}> {
  const facetOnly = {
    hitsPerPage: 0,
    facets: ['parentOrg'],
    maxValuesPerFacet: 1000,
    analytics: false,
    cacheable: false,
  };
  const [brands, manufacturers] = await Promise.all([
    getClient().initIndex(BRANDS_INDEX).search('', facetOnly),
    getClient().initIndex(MANUFACTURERS_INDEX).search('', facetOnly),
  ]);
  const read = (facets: unknown): Record<string, number> =>
    ((facets as Record<string, Record<string, number>> | undefined)?.parentOrg ?? {});
  return { brands: read(brands.facets), manufacturers: read(manufacturers.facets) };
}

/** One brand or manufacturer document an organization owns, as the index holds it. */
export interface ParentOrgPortfolioHit {
  id: string;
  name: string;
  /** The raw `parentOrg` string on the document that matched. */
  matchedValue: string;
}

/**
 * The brands and manufacturers carrying any of `values` as their legacy
 * `parentOrg` string.
 *
 * Two facet-filtered searches, no Firestore reads — the same trick as
 * {@link getParentOrgUsageCounts}, one step further: that one asks how MANY,
 * this one asks which. `values` must be the RAW stored strings (from the facet
 * map), because `facetFilters` match a stored value exactly; a slugged key
 * matches nothing. Entries within the array are OR-ed.
 *
 * `hitsPerPage` is generous rather than paged: the largest organization in the
 * catalogue owns ~20 brands, and a portfolio that needed paging would be a
 * different screen anyway.
 *
 * Caveat worth knowing at the call site: this sees only what is INDEXED. A
 * disabled brand is evicted from the index, so it will not appear here even
 * though its document still points at the organization.
 */
export async function getParentOrgPortfolio(values: readonly string[]): Promise<{
  brands: ParentOrgPortfolioHit[];
  manufacturers: ParentOrgPortfolioHit[];
}> {
  if (values.length === 0) return { brands: [], manufacturers: [] };
  const params = {
    hitsPerPage: 200,
    attributesToRetrieve: ['objectID', 'name', 'displayName', 'parentOrg'],
    facetFilters: [values.map((v) => `parentOrg:${v}`)],
    analytics: false,
  };
  const [brands, manufacturers] = await Promise.all([
    getClient().initIndex(BRANDS_INDEX).search('', params),
    getClient().initIndex(MANUFACTURERS_INDEX).search('', params),
  ]);
  type RawHit = {
    objectID: string;
    name?: string;
    displayName?: string;
    parentOrg?: string;
  };
  const read = (hits: unknown): ParentOrgPortfolioHit[] =>
    (hits as RawHit[]).map((h) => ({
      id: h.objectID,
      name: h.name || h.displayName || h.objectID,
      matchedValue: h.parentOrg ?? '',
    }));
  return { brands: read(brands.hits), manufacturers: read(manufacturers.hits) };
}

/** A module hit for the reference-editor module typeahead. */
export interface ElectronicModuleHit {
  objectID: string;
  name: string;
  manufacturerId?: string;
  displayType?: string;
  battery?: string;
}

/**
 * Typeahead search of `watchkeeper_electronic_modules` for the WatchReference
 * editor's module picker. Mirrors `searchManufacturerCalibres` minus the
 * manufacturer scoping — a digital ref can carry any maker's module, so the
 * picker searches the whole index by name/alias and shows maker + display as
 * disambiguating context.
 */
export async function searchElectronicModules(
  queryStr: string,
  opts?: { hitsPerPage?: number },
): Promise<ElectronicModuleHit[]> {
  const hitsPerPage = opts?.hitsPerPage ?? 10;
  type RawHit = {
    objectID: string;
    name?: string;
    displayName?: string;
    manufacturerId?: string;
    displayType?: string;
    battery?: string;
  };
  const res = await getClient()
    .initIndex(ELECTRONIC_MODULES_INDEX)
    .search<RawHit>(queryStr, {
      hitsPerPage,
      removeWordsIfNoResults: 'allOptional',
      attributesToRetrieve: [
        'objectID',
        'name',
        'displayName',
        'manufacturerId',
        'displayType',
        'battery',
      ],
    });
  return res.hits.map((h) => ({
    objectID: h.objectID,
    name: h.name || h.displayName || h.objectID,
    manufacturerId: h.manufacturerId,
    displayType: h.displayType,
    battery: h.battery,
  }));
}

/**
 * A calibre-family or calibre hit, normalized for the "inspired by" picker.
 * `kind` distinguishes the two so the caller can build the structured
 * CalibreFamilyInspiredBy FK (family → familyId, calibre → calibreId).
 */
export interface InspiredByRefHit {
  objectID: string;
  kind: 'family' | 'calibre';
  name: string;
  manufacturerId?: string;
}

/**
 * Algolia's per-hit ranking metadata (requested via `getRankingInfo: true`).
 * Only the fields we use to merge-sort across the two indexes are typed.
 */
interface HitRankingInfo {
  nbTypos: number;
  firstMatchedWord: number;
  proximityDistance: number;
  words: number;
  nbExactWords: number;
}

/**
 * Compare two hits by Algolia relevance so we can merge results from two
 * separate indexes into one "best match first" list. Mirrors Algolia's own
 * tie-break order (typo → words → proximity → attribute → exact), which is the
 * only principled way to rank a family hit against a calibre hit when each index
 * computed its score independently. Missing ranking info sorts last.
 */
function compareByRanking(a?: HitRankingInfo, b?: HitRankingInfo): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return (
    a.nbTypos - b.nbTypos || // fewer typos first
    b.words - a.words || // more matched (optional) words first
    a.proximityDistance - b.proximityDistance || // tighter word proximity first
    a.firstMatchedWord - b.firstMatchedWord || // earlier attribute match first
    b.nbExactWords - a.nbExactWords // more exact-word matches first
  );
}

/**
 * Search calibre families AND calibres in parallel for the inspired-by picker.
 * Cross-manufacturer lineage is the point, so we don't filter by manufacturer
 * here (the caller may want to exclude self, but that's a display concern).
 * Field names are read defensively since the two indexes differ.
 *
 * Two relevance concerns are handled here rather than in the index config:
 *   1. The calibre index's `searchableAttributes` are `name` + `alsoKnownAs`
 *      only — the manufacturer name is NOT searchable — so a brand-prefixed
 *      query like "ETA 2824" would AND-fail (no calibre record contains "ETA").
 *      `removeWordsIfNoResults: 'allOptional'` retries with every word optional,
 *      so the discriminating token ("2824") still surfaces the calibre. Same for
 *      families (the manufacturer name isn't reliably in their searchable fields).
 *   2. The two indexes score independently, so we request `getRankingInfo` and
 *      merge-sort by Algolia relevance (see compareByRanking) instead of always
 *      listing families before calibres — a perfect calibre match now outranks a
 *      weak family match.
 */
export async function searchInspiredByRefs(
  queryStr: string,
  opts?: { hitsPerPage?: number },
): Promise<InspiredByRefHit[]> {
  const hitsPerPage = opts?.hitsPerPage ?? 6;
  const client = getClient();
  type RawHit = {
    objectID: string;
    name?: string;
    displayName?: string;
    manufacturerId?: string;
    _rankingInfo?: HitRankingInfo;
  };
  const searchParams = {
    hitsPerPage,
    removeWordsIfNoResults: 'allOptional' as const,
    getRankingInfo: true,
  };
  const [famRes, calRes] = await Promise.all([
    client.initIndex(CALIBRE_FAMILIES_INDEX).search<RawHit>(queryStr, searchParams),
    client.initIndex(CALIBRES_INDEX).search<RawHit>(queryStr, searchParams),
  ]);

  type RankedHit = InspiredByRefHit & { _rankingInfo?: HitRankingInfo };
  const families: RankedHit[] = famRes.hits.map((h) => ({
    objectID: h.objectID,
    kind: 'family',
    name: h.displayName || h.name || h.objectID,
    manufacturerId: h.manufacturerId,
    _rankingInfo: h._rankingInfo,
  }));
  const calibres: RankedHit[] = calRes.hits.map((h) => ({
    objectID: h.objectID,
    kind: 'calibre',
    name: h.name || h.displayName || h.objectID,
    manufacturerId: h.manufacturerId,
    _rankingInfo: h._rankingInfo,
  }));

  // Merge both indexes and order by Algolia relevance (best overall match
  // first), so a strong calibre match can outrank a weak family match and vice
  // versa. Array.prototype.sort is stable, so equal-ranked hits keep their
  // original family-then-calibre order. Strip the internal _rankingInfo before
  // returning so the public shape stays InspiredByRefHit.
  return [...families, ...calibres]
    .sort((a, b) => compareByRanking(a._rankingInfo, b._rankingInfo))
    .map(({ _rankingInfo: _drop, ...hit }) => hit);
}

/** A calibre hit scoped to one manufacturer, for the base-calibre typeahead. */
export interface ManufacturerCalibreHit {
  objectID: string;
  name: string;
  manufacturerId?: string;
}

/**
 * Typeahead search of `watchkeeper_calibres` scoped to a single manufacturer —
 * matches calibres whose primary `manufacturerId` is the manufacturer OR whose
 * denormalised `allManufacturerIds` set contains it (joint authorship, plus the
 * heritage ancestors of each maker).
 *
 * Scoping is done client-side on the retrieved attributes rather than via an
 * Algolia `filters` clause: the calibres index's faceting config is owned
 * server-side (watchlock) and we don't want a non-faceted-attribute filter to
 * 400 the whole search. Searching by the typed calibre name keeps the unfiltered
 * result set small and relevant, so a generous `hitsPerPage` then a client-side
 * manufacturer filter is reliable and avoids a facet dependency. This also means
 * the picker keeps working even when the Firestore `manufacturerId ==` list
 * comes back empty (see CalibreFamiliesSection base-calibre picker).
 *
 * The joint-authorship arm used to read `additionalManufacturerIds`, which
 * `buildCalibreIndexRecord` has never emitted — the co-developer set is indexed
 * only as `allManufacturerIds` (watchlock #705). The field was therefore always
 * `undefined` here and every co-developed calibre was silently dropped from the
 * typeahead unless its PRIMARY maker matched.
 */
export async function searchManufacturerCalibres(
  queryStr: string,
  manufacturerId: string,
  opts?: { hitsPerPage?: number },
): Promise<ManufacturerCalibreHit[]> {
  if (!manufacturerId) return [];
  const hitsPerPage = opts?.hitsPerPage ?? 50;
  type RawHit = {
    objectID: string;
    name?: string;
    displayName?: string;
    manufacturerId?: string;
    allManufacturerIds?: string[] | null;
  };
  const res = await getClient()
    .initIndex(CALIBRES_INDEX)
    .search<RawHit>(queryStr, {
      hitsPerPage,
      attributesToRetrieve: [
        'objectID',
        'name',
        'displayName',
        'manufacturerId',
        'allManufacturerIds',
      ],
    });
  return res.hits
    .filter(
      (h) =>
        h.manufacturerId === manufacturerId ||
        (Array.isArray(h.allManufacturerIds) &&
          h.allManufacturerIds.includes(manufacturerId)),
    )
    .map((h) => ({
      objectID: h.objectID,
      name: h.name || h.displayName || h.objectID,
      manufacturerId: h.manufacturerId,
    }));
}

/**
 * Doc ids of calibres whose name or alias plausibly matches `name`.
 *
 * A NARROWING pass, not an answer: the caller loads these few `custom_calibres`
 * documents and runs the authoritative match (`calibreEntryMatchesRef` — brand
 * scope, catch-all and placeholder rules, `normalizeCalibreKey` folding) against
 * the real docs. The index carries `name` + `alsoKnownAs` as searchable
 * attributes but NOT `aliasDetails`, `isCatchAll` or `deprecated`, so it can
 * never decide a match on its own.
 *
 * This replaces a `getDocs(custom_calibres)` full scan — ~1,822 documents, on
 * every ref-editor mount and again on every keystroke in the Calibre input (see
 * docs/audits/ref-editor-listener-audit-2026-08-05.md, fix #1).
 *
 * `removeWordsIfNoResults: 'allOptional'` because a stored calibre string is
 * often brand-prefixed ("Sellita SW200-1") while the DB record is not, or vice
 * versa: an AND over every word would return nothing. Typo tolerance covers the
 * Caliber/Calibre spelling fold that `normalizeCalibreKey` applies locally.
 *
 * Throws if Algolia is unconfigured or the request fails — callers must fall
 * back to the full scan rather than reporting "not in the DB".
 */
export async function searchCalibreIdsByName(
  name: string,
  opts?: { hitsPerPage?: number },
): Promise<string[]> {
  const trimmed = name.trim();
  if (!trimmed) return [];
  const response = await getClient()
    .initIndex(CALIBRES_INDEX)
    .search<{ objectID: string }>(trimmed, {
      hitsPerPage: opts?.hitsPerPage ?? 10,
      removeWordsIfNoResults: 'allOptional',
      analytics: false,
      attributesToRetrieve: ['objectID'],
      attributesToHighlight: [],
    });
  return response.hits.map((h) => h.objectID);
}

/** One `watchkeeper_calibres` record, as the admin's pickers consume it. */
export interface CalibreHit {
  /** The calibre's Firestore doc id — `custom_calibres/{objectID}`. */
  objectID: string;
  name?: string;
  alsoKnownAs?: string[];
  brandId?: string;
  brandName?: string;
  manufacturerId?: string;
  /** Primary maker + co-developers + each one's heritage ancestors. */
  allManufacturerIds?: string[];
  movementType?: string;
  isInHouse?: boolean;
  referenceCount?: number;
  familyId?: string;
}

/** One page of calibre search results, plus any facet counts that were asked for. */
export interface CalibreSearchResult {
  hits: CalibreHit[];
  /** True match count for the query + filters, independent of the page size. */
  totalHits: number;
  /** Empty unless `opts.facets` was passed. */
  facets: Record<string, Record<string, number>>;
  page: number;
  totalPages: number;
}

/**
 * Search `watchkeeper_calibres` — the fuzzy calibre lookup the admin's pickers
 * run on.
 *
 * This is what replaces the `getCustomCalibres()` full collection scan those
 * pickers used to run — ~1,822 documents on every open, then filtered locally
 * with `.includes()`, which demanded near-exact spelling. The index searches
 * `name` and `alsoKnownAs` with typo tolerance and accent folding, so a
 * misremembered grade still hits.
 *
 * A NARROWING pass, not an answer, for anything that has to DECIDE something:
 * the index carries neither `aliasDetails` (brand scope) nor `isCatchAll` (see
 * `CALIBRE_FIELDS_NOT_INDEXED`), so callers that render or act on a calibre
 * hydrate the hit ids back to real documents — which is what `useCalibreSearch`
 * does — rather than trusting the hit alone. The one caller that does NOT
 * hydrate is the listing, which already holds every record (see
 * `fetchCalibreListing`) and only needs Algolia to rank ids.
 *
 * Searchable attributes are `name` and `alsoKnownAs`, plus the denormalised
 * `manufacturerName` / `brandNames` / `familyName` (watchlock #864) at a lower
 * priority — so a query matching the calibre itself still outranks one that only
 * matches its maker or family.
 *
 * Throws if Algolia is unconfigured or the request fails; callers must fall back
 * to a Firestore read rather than reporting "no such calibre". Note the index
 * holds only calibres the indexer has caught up with, so a just-created calibre
 * can be missing for a few seconds.
 */
export async function searchCalibres(
  query: string,
  filters: CalibreSearchFilters = {},
  page: number = 0,
  hitsPerPage: number = 50,
  opts?: { attributes?: readonly string[]; facets?: readonly string[] },
): Promise<CalibreSearchResult> {
  const response = await getClient()
    .initIndex(CALIBRES_INDEX)
    .search<CalibreHit>(
      query.trim(),
      buildCalibreSearchParams(filters, page, hitsPerPage, opts),
    );
  return {
    hits: response.hits,
    totalHits: response.nbHits,
    facets: (response.facets ?? {}) as Record<string, Record<string, number>>,
    page: response.page,
    totalPages: response.nbPages,
  };
}

/**
 * How many records match a bucket, without downloading any of them.
 * `hitsPerPage: 0` — `nbHits` only, which the live index reports as
 * `exhaustiveNbHits` (an exact count, not an estimate).
 */
async function countCalibreBucket(
  calibresIndex: SearchIndex,
  filters: readonly string[],
): Promise<number> {
  const response = await calibresIndex.search('', {
    ...buildCalibreListParams(filters, 0),
    attributesToRetrieve: [],
  });
  return response.nbHits;
}

/**
 * One disjoint slice of the index, split further until each piece fits in a
 * single query. `count` is the caller's already-known size for this bucket, so
 * no bucket is ever counted twice.
 *
 * Counts BEFORE it fetches, because an oversized bucket's hits are thrown away:
 * probing with `hitsPerPage: 0` costs a round trip but no payload, whereas
 * fetching first and discarding would download 1,000 records per split.
 */
async function fetchCalibreBucket(
  calibresIndex: SearchIndex,
  filters: readonly string[],
  depth: number,
  count: number,
): Promise<CalibreListingHit[]> {
  if (count === 0) return [];
  if (count <= CALIBRE_PAGINATION_LIMIT) {
    const response = await calibresIndex.search<CalibreListingHit>(
      '',
      buildCalibreListParams(filters, CALIBRE_PAGINATION_LIMIT),
    );
    return response.hits;
  }
  if (depth >= CALIBRE_PARTITION_FACETS.length) {
    // Every dimension spent and still too big. Refusing here is the point:
    // returning the first 1,000 would look exactly like a complete catalogue.
    throw new Error(
      `Calibre listing bucket [${filters.join(' AND ') || 'all'}] holds ${count} ` +
        `records, more than the ${CALIBRE_PAGINATION_LIMIT} one Algolia query can ` +
        'return, and there are no partition facets left to split it by. Raise ' +
        '`paginationLimitedTo` on watchkeeper_calibres in watchlock.',
    );
  }
  const facet = CALIBRE_PARTITION_FACETS[depth];
  const halves = [
    [...filters, `${facet}:true`],
    [...filters, `${facet}:false`],
  ];
  const counts = await Promise.all(
    halves.map((f) => countCalibreBucket(calibresIndex, f)),
  );
  const [truthy, falsy] = await Promise.all(
    halves.map((f, i) =>
      fetchCalibreBucket(calibresIndex, f, depth + 1, counts[i]),
    ),
  );
  return [...truthy, ...falsy];
}

/**
 * Every calibre in `watchkeeper_calibres`, as the calibre LISTING consumes it.
 *
 * This is what replaces the listing's `getCustomCalibres()` — ~1,823 documents
 * read from Firestore on every mount of the Curated Calibres tab, plus a
 * Storage `getDownloadURL` round trip per visible thumbnail. The whole index
 * comes back in a handful of search requests, which is why the listing can keep
 * filtering, sorting, grouping, paginating and totalling locally exactly as it
 * did before: it still holds every record, just not from Firestore.
 *
 * Assembled from disjoint BUCKETS rather than from pages, because
 * `paginationLimitedTo` on this index is Algolia's default 1,000 and the index
 * holds 1,823 — see {@link CALIBRE_PAGINATION_LIMIT}. A paged loop is not merely
 * incomplete here, it is SILENTLY incomplete: `nbPages` comes back as 1, so the
 * loop terminates believing it has everything. `fetchBrandListing` pages happily
 * only because 561 brands fit under the same ceiling.
 *
 * Deliberately NOT server-paginated against the sort replicas either, even
 * though six exist (`name`, `refCount`, `createdAt` — asc and desc each). Four
 * of the listing's features cannot be expressed server-side at all:
 *
 * - The Group-by-brand dropdown needs every distinct brand key. `brandId` and
 *   `brandName` are both `filterOnly`, so neither returns facet values.
 * - The generic / non-generic Type filter reads `isGeneric` off the MANUFACTURER
 *   documents, which are a different collection entirely.
 * - The Manufacturer sort orders by resolved manufacturer NAME, and the Image
 *   sort by `hasImage` — neither has a replica, and `hasImage` is `filterOnly`.
 * - The footer's "N of M" is a count over the whole filtered set.
 *
 * The returned order is bucket order, not any global ranking. That is fine and
 * deliberate: the listing sorts locally by its own sort control, which is the
 * same reason the sort replicas are unused.
 *
 * Throws if Algolia is unconfigured, a request fails, a bucket cannot be split
 * small enough, or the assembled total falls materially short of the index-wide
 * count. Every one of those makes the caller fall back to the Firestore
 * collection read — slow and complete beats fast and quietly missing rows.
 *
 * NOTE this returns DEPRECATED calibres too (redirect aliases), which
 * `getCustomCalibres()` filters out by default. The listing has a "show
 * deprecated" toggle and applies the exclusion itself — fetching them once means
 * the toggle costs no round trip.
 */
export async function fetchCalibreListing(): Promise<CalibreListingHit[]> {
  const calibresIndex = getClient().initIndex(CALIBRES_INDEX);
  const expected = await countCalibreBucket(calibresIndex, []);
  const hits = await fetchCalibreBucket(calibresIndex, [], 0, expected);

  // The buckets partition the index, so ids cannot repeat — dedupe anyway, so a
  // record that somehow matched two buckets shows as one row rather than two.
  const byId = new Map<string, CalibreListingHit>();
  for (const hit of hits) byId.set(hit.objectID, hit);
  const unique = [...byId.values()];

  const shortfall = expected - unique.length;
  if (shortfall > CALIBRE_PARTITION_DRIFT_TOLERANCE) {
    // A record missing one of the partition attributes matches neither half of
    // that split and is dropped by both. Fail loudly rather than render a
    // catalogue with holes the admin has no way to notice.
    throw new Error(
      `Calibre listing assembled ${unique.length} of ${expected} records — ` +
        `${shortfall} fell outside every partition bucket. Some records are ` +
        'likely missing a CALIBRE_PARTITION_FACETS attribute; re-run the ' +
        'watchkeeper_calibres backfill in watchlock.',
    );
  }
  return unique;
}

/**
 * How many calibres each manufacturer is the PRIMARY maker of, keyed by
 * manufacturer id.
 *
 * This is what replaces `getCalibreCountsByManufacturerId()` — a
 * `getDocs(custom_calibres)` full scan (~1,822 documents) run on every mount of
 * the Movement Manufacturers tab purely to render one number per row. Here it is
 * a single search returning no hits at all, just the `manufacturerId` facet
 * counts, which the index computes server-side.
 *
 * Counts the same population the Firestore tally did: every indexed calibre with
 * a primary `manufacturerId`, deprecated ones included (the index carries no
 * `deprecated` field to exclude them by, and the scan it replaces didn't filter
 * either). Co-developer credits are NOT counted, matching the old behaviour and
 * `getCalibresCountForManufacturer`.
 *
 * `maxValuesPerFacet` caps at Algolia's ceiling of 1,000 distinct manufacturers.
 * Beyond that the response is truncated and the tail of the list would show no
 * count, so the caller keeps its Firestore fallback for a wrong-looking answer;
 * the manufacturer catalogue is in the low hundreds today.
 *
 * Throws if Algolia is unconfigured or the request fails.
 */
export async function getCalibreCountsByManufacturer(): Promise<
  Map<string, number>
> {
  const response = await getClient()
    .initIndex(CALIBRES_INDEX)
    .search<CalibreHit>('', {
      hitsPerPage: 0,
      analytics: false,
      cacheable: false,
      facets: ['manufacturerId'],
      maxValuesPerFacet: ALGOLIA_MAX_FACET_VALUES,
      attributesToHighlight: [],
    });
  const counts = new Map<string, number>();
  const facet = response.facets?.manufacturerId ?? {};
  for (const [manufacturerId, count] of Object.entries(facet)) {
    if (manufacturerId) counts.set(manufacturerId, count);
  }
  return counts;
}

/** A brand record as the admin's pickers and listing consume it. */
export interface BrandHit {
  /** The brand's Firestore doc id — `watchBrands/{objectID}`. */
  objectID: string;
  name?: string;
  displayName?: string;
  country?: string;
  parentOrg?: string;
  alternativeNames?: string[];
  abbreviations?: string[];
  hasLogo?: boolean;
  isDisabled?: boolean;
  isPromoted?: boolean;
}

/** One page of brand search results, plus any facet counts that were asked for. */
export interface BrandSearchResult {
  hits: BrandHit[];
  /** True match count for the query + filters, independent of the page size. */
  totalHits: number;
  /** Empty unless `opts.facets` was passed. */
  facets: Record<string, Record<string, number>>;
  page: number;
  totalPages: number;
}

/**
 * Search `watchkeeper_brands` — the fuzzy brand lookup every admin picker runs
 * on.
 *
 * This is what replaces the `.includes()` filters the pickers used to run over a
 * locally loaded brand list. Those demanded exact spelling: "journe" missed
 * "F.P.Journe" (no prefix matching), "raul pages" missed "Raúl PAGÈS" (no accent
 * folding), a one-character typo missed everything, and "LVMH" missed all seven
 * of its brands because the parent org isn't part of the name. The index
 * searches `displayName`, `name`, `alternativeNames`, `abbreviations` and
 * `parentOrg` with typo tolerance and accent folding (watchlock #861), so all
 * four now hit.
 *
 * `opts.attributes` narrows the response — callers that only need to know WHICH
 * brands matched (because they already hold the full documents) pass
 * `['objectID']` and download almost nothing per keystroke.
 *
 * Throws if Algolia is unconfigured or the request fails; callers must fall back
 * to a local filter rather than reporting "no such brand". Note the index holds
 * only brands the indexer has caught up with, so a just-created brand can be
 * missing — `mergeBrandSearchResults` is what keeps those findable.
 */
export async function searchBrands(
  query: string,
  filters: BrandSearchFilters = {},
  page: number = 0,
  hitsPerPage: number = 50,
  opts?: {
    attributes?: readonly string[];
    facets?: readonly BrandSearchFacetName[];
  },
): Promise<BrandSearchResult> {
  const response = await getClient()
    .initIndex(BRANDS_INDEX)
    .search<BrandHit>(
      query.trim(),
      buildBrandSearchParams(filters, page, hitsPerPage, opts),
    );
  return {
    hits: response.hits,
    totalHits: response.nbHits,
    facets: (response.facets ?? {}) as Record<string, Record<string, number>>,
    page: response.page,
    totalPages: response.nbPages,
  };
}

/**
 * Algolia's per-request ceiling. The brands index holds 561 records, so the
 * listing is one request today; the loop below exists so it stays one code path
 * if the catalogue grows past a page.
 */
const BRAND_LIST_PAGE_SIZE = 1000;

/**
 * Runaway guard on that loop — 10,000 brands is an order of magnitude beyond any
 * plausible catalogue, so hitting it means the pagination logic is wrong, not
 * that the data grew.
 */
const BRAND_LIST_MAX_PAGES = 10;

/**
 * Every brand in `watchkeeper_brands`, as the brands LISTING consumes it.
 *
 * This is what replaces the listing's `getDocs(watchBrands)` — 561 documents
 * read from Firestore on every mount of the Brands tab. The whole index comes
 * back in a single search request (~190 KB uncompressed, far less on the wire),
 * which is why the listing can keep filtering, sorting, paginating and totalling
 * locally exactly as it did before: it still holds every record, just not from
 * Firestore.
 *
 * Deliberately NOT server-paginated against the sort replicas. The index has six
 * (`parentReferenceCount`, `createdAt`, `lastImportedAt` — asc and desc each,
 * watchlock #863) but NONE for brand name or country, which are two of the five
 * sorts this listing offers and one of them is the default. Server pagination
 * would therefore have to fall back to a whole-index fetch for those two anyway,
 * leaving two divergent code paths; and the stats bar's "Total refs" is a SUM of
 * `parentReferenceCount` across every brand, which no facet can express.
 *
 * Throws if Algolia is unconfigured or the request fails; the caller falls back
 * to the Firestore collection read rather than showing an empty catalogue.
 *
 * NOTE the index omits disabled brands entirely — the caller has to top those up
 * from Firestore or they vanish from the admin's only view of them.
 */
export async function fetchBrandListing(): Promise<BrandListingHit[]> {
  const brandsIndex = getClient().initIndex(BRANDS_INDEX);
  const all: BrandListingHit[] = [];
  let page = 0;
  let totalPages = 1;
  while (page < totalPages && page < BRAND_LIST_MAX_PAGES) {
    const response = await brandsIndex.search<BrandListingHit>(
      '',
      buildBrandListParams(page, BRAND_LIST_PAGE_SIZE),
    );
    all.push(...response.hits);
    totalPages = response.nbPages;
    page += 1;
  }
  return all;
}

/**
 * Algolia's per-request ceiling. `watchkeeper_manufacturers` holds 176 records
 * and its `paginationLimitedTo` was raised to 5,000, so the catalogue is one
 * request today; the loop below exists so it stays one code path if the
 * catalogue grows past a page.
 */
const MANUFACTURER_PAGE_SIZE = 1000;

/**
 * Runaway guard on that loop. Ten pages is 10,000 manufacturers — two orders of
 * magnitude beyond any plausible catalogue, so hitting it means the pagination
 * logic is wrong, not that the data grew.
 */
const MANUFACTURER_MAX_PAGES = 10;

/**
 * Every manufacturer in `watchkeeper_manufacturers`, name-sorted.
 *
 * This is what replaces `getMovementManufacturers()` — an `orderBy('name')` read
 * of all 176 `movement_manufacturers` documents, which the admin ran on every
 * open of the reference editor's movement panel, the calibre editor, the
 * electronic-module editor, the Curated Calibres tab and three more surfaces
 * besides. The whole index comes back in a single search request, which is why
 * every one of those consumers keeps rendering its `<select>` and its
 * `id → name` map exactly as before: it still holds every record, just not from
 * Firestore.
 *
 * Deliberately NOT server-paginated or server-filtered against the four sort
 * replicas that exist (`createdAt` / `updatedAt`, asc and desc — watchlock
 * #866). Every consumer needs the whole catalogue at once to render a dropdown
 * or resolve arbitrary FKs to names, and none of them sorts by a date.
 *
 * Unlike `fetchCalibreListing`, no bucket partitioning: a paged loop is safe
 * here because 176 records sit far under `paginationLimitedTo`, so `nbPages` is
 * an honest 1 rather than the silent truncation the calibres index hits.
 *
 * Throws if Algolia is unconfigured or the request fails — callers fall back to
 * the Firestore collection read rather than showing an empty dropdown. See
 * `getManufacturerCatalogue` in `firebase.ts`, which is what every consumer
 * actually calls.
 *
 * NOTE the entries are a PROJECTION, not whole documents: five fields the index
 * does not carry are listed in `MANUFACTURER_FIELDS_NOT_INDEXED`, and
 * `ManufacturerCatalogueEntry` is typed narrowly so reaching for one is a
 * compile error.
 */
export async function fetchManufacturerCatalogue(): Promise<
  ManufacturerCatalogueEntry[]
> {
  const manufacturersIndex = getClient().initIndex(MANUFACTURERS_INDEX);
  const all: ManufacturerCatalogueEntry[] = [];
  let page = 0;
  let totalPages = 1;
  while (page < totalPages && page < MANUFACTURER_MAX_PAGES) {
    const response = await manufacturersIndex.search<ManufacturerCatalogueHit>(
      '',
      buildManufacturerCatalogueParams(page, MANUFACTURER_PAGE_SIZE),
    );
    all.push(...response.hits.map(manufacturerHitToEntry));
    totalPages = response.nbPages;
    page += 1;
  }
  return sortManufacturersByName(all);
}

/**
 * Algolia's per-request ceiling for the winder catalogue. `watchkeeper_winders`
 * holds a few dozen records against a `paginationLimitedTo` of 5,000
 * (`pushAlgoliaWinderIndexSettings`, watchlock), so this is one request today;
 * the loop below exists so it stays one code path if the catalog grows past a
 * page.
 */
const WINDER_PAGE_SIZE = 1000;

/**
 * Runaway guard on that loop. Ten pages is 10,000 winders — orders of magnitude
 * beyond any plausible catalog, so hitting it means the pagination logic is
 * wrong, not that the data grew.
 */
const WINDER_MAX_PAGES = 10;

/**
 * Every winder in `watchkeeper_winders`, model-sorted.
 *
 * This is what replaces `getWinders()` — an `orderBy('model')` read of the whole
 * `winders` collection, which the admin ran on every open of the Curated Winders
 * tab and again after every bulk write. The whole index comes back in a single
 * search request, which is why the listing keeps its client-side search, its
 * deprecated toggle and its column sorts unchanged: it still holds every record,
 * just not from Firestore.
 *
 * The entries are WHOLE winder documents, not a projection — see
 * `winderCatalogueQuery.ts` for why that is load-bearing rather than incidental.
 *
 * Throws if Algolia is unconfigured or the request fails; `getWinderCatalogue`
 * in `firebase.ts` is what callers use, and it falls back to the Firestore
 * collection read.
 */
export async function fetchWinderCatalogue(): Promise<WinderCatalogueEntry[]> {
  const windersIndex = getClient().initIndex(WINDERS_INDEX);
  const all: WinderCatalogueEntry[] = [];
  let page = 0;
  let totalPages = 1;
  while (page < totalPages && page < WINDER_MAX_PAGES) {
    const response = await windersIndex.search<WinderCatalogueHit>(
      '',
      buildWinderCatalogueParams(page, WINDER_PAGE_SIZE),
    );
    all.push(...response.hits.map(winderHitToEntry));
    totalPages = response.nbPages;
    page += 1;
  }
  return sortWindersByModel(all);
}

/**
 * Normalize reference number for search (remove spaces, dashes, slashes)
 */
export function normalizeReference(query: string): string {
  return query
    .trim()
    .toUpperCase()
    .replace(/[\s\-\/\.]/g, '');
}

/**
 * Check if query looks like a reference number
 */
export function looksLikeReference(query: string): boolean {
  const trimmed = query.trim();
  if (!/\d/.test(trimmed)) {
    return false;
  }
  return /^[\w\s\-\/\.]+$/.test(trimmed);
}

export interface SearchParams {
  query: string;
  brandId?: string;
  collection?: string;
  limit?: number;
  page?: number;
  exactMatch?: boolean;
  facetFilters?: string[][];
  numericFilters?: string[];
}

/**
 * Search for watch references by reference number
 */
export async function searchByReference(
  params: SearchParams,
): Promise<AlgoliaSearchResponse<WatchReferenceAlgoliaHit>> {
  const { query, brandId, limit = 20, page = 0, exactMatch = false } = params;

  const trimmedQuery = query.trim();
  const normalizedQuery = normalizeReference(trimmedQuery);

  const searchParams: Record<string, unknown> = {
    hitsPerPage: limit,
    page,
    attributesToRetrieve: [
      'objectID',
      'brandId',
      'referenceId',
      'reference',
      'referenceNormalized',
      'model',
      'collection',
      'productName',
      'calibre',
      'caseSizeMm',
      'productionStartYear',
      'productionEndYear',
      'imageUrl',
    ],
    attributesToHighlight: ['reference', 'model', 'collection', 'productName'],
    restrictSearchableAttributes: ['reference', 'referenceNormalized'],
  };

  if (exactMatch) {
    searchParams.typoTolerance = false;
    searchParams.removeWordsIfNoResults = 'none';
  }

  const filters: string[] = [];
  if (brandId) {
    filters.push(`brandId:${brandId}`);
  }
  if (filters.length > 0) {
    searchParams.filters = filters.join(' AND ');
  }

  if (!exactMatch && looksLikeReference(trimmedQuery)) {
    searchParams.optionalWords = normalizedQuery;
  }

  const searchQuery = exactMatch ? normalizedQuery : trimmedQuery;
  const response = await getIndex().search<WatchReferenceAlgoliaHit>(
    searchQuery,
    searchParams,
  );

  return {
    hits: response.hits,
    nbHits: response.nbHits,
    page: response.page,
    nbPages: response.nbPages,
    hitsPerPage: response.hitsPerPage,
    processingTimeMS: response.processingTimeMS,
    query: response.query,
    facets: response.facets,
  };
}

/**
 * Perform general search across all fields
 */
export async function searchGeneral(
  params: SearchParams,
): Promise<AlgoliaSearchResponse<WatchReferenceAlgoliaHit>> {
  const {
    query,
    brandId,
    collection,
    limit = 20,
    page = 0,
    facetFilters: extraFacetFilters,
    numericFilters,
  } = params;

  const searchParams: Record<string, unknown> = {
    hitsPerPage: limit,
    page,
    attributesToRetrieve: [
      'objectID',
      'brandId',
      'referenceId',
      'reference',
      'model',
      'collection',
      'productName',
      'calibre',
      'caseSizeMm',
      'productionStartYear',
      'productionEndYear',
      'imageUrl',
    ],
    attributesToHighlight: ['reference', 'model', 'collection', 'productName'],
    facets: ['brandId', 'collection'],
  };

  const facetFilters: string[][] = [];
  if (brandId) {
    facetFilters.push([`brandId:${brandId}`]);
  }
  if (collection) {
    facetFilters.push([`collection:${collection}`]);
  }
  if (extraFacetFilters && extraFacetFilters.length > 0) {
    facetFilters.push(...extraFacetFilters);
  }
  if (facetFilters.length > 0) {
    searchParams.facetFilters = facetFilters;
  }
  if (numericFilters && numericFilters.length > 0) {
    searchParams.numericFilters = numericFilters;
  }

  const response = await getIndex().search<WatchReferenceAlgoliaHit>(
    query.trim(),
    searchParams,
  );

  return {
    hits: response.hits,
    nbHits: response.nbHits,
    page: response.page,
    nbPages: response.nbPages,
    hitsPerPage: response.hitsPerPage,
    processingTimeMS: response.processingTimeMS,
    query: response.query,
    facets: response.facets,
  };
}

/**
 * Smart search that auto-detects query type
 */
export async function search(
  params: SearchParams,
): Promise<AlgoliaSearchResponse<WatchReferenceAlgoliaHit>> {
  if (looksLikeReference(params.query)) {
    return searchByReference(params);
  }
  return searchGeneral(params);
}

/**
 * Total number of indexed references for a brand. Runs an empty query filtered
 * by `brandId` and reads `nbHits` only (hitsPerPage: 0 — no hits downloaded), so
 * it's a single cheap call. Used by the brand portal dashboard's References
 * metric; callers should fall back to a Firestore count if this throws.
 */
export async function getBrandReferenceCount(brandId: string): Promise<number> {
  const response = await getIndex().search('', {
    filters: `brandId:${brandId}`,
    hitsPerPage: 0,
    analytics: false,
  });
  return response.nbHits;
}

/**
 * How many hits to pull when building a sibling list. Over-fetches well past
 * MAX_SIBLING_REFERENCES so that excluding the ref being enriched and deduping
 * repeated reference strings can't leave the prompt short, and so the
 * nickname-first ordering sees the whole of a large collection before the cap,
 * while keeping the response small (eight attributes per hit).
 */
const SIBLING_FETCH_HITS = 200;

/**
 * One search for the references in a brand's collection.
 *
 * `collection` is free text and facet matching is exact, so the filter asks for
 * the stored value and its common casings in one OR group ("ZULU", "Zulu",
 * "zulu"); `siblingReferencesFromHits` then applies the real case-insensitive
 * comparison. `model`, `nickname` and `collection` are retrieved so each sibling
 * can be handed to the model with what it IS, not just its number.
 */
async function searchSiblingHits(
  brandId: string,
  collectionName: string,
): Promise<SiblingReferenceHit[]> {
  const response = await getIndex().search<WatchReferenceAlgoliaHit>('', {
    facetFilters: [
      [`brandId:${brandId}`],
      collectionQueryVariants(collectionName).map((spelling) => `collection:${spelling}`),
    ],
    hitsPerPage: SIBLING_FETCH_HITS,
    analytics: false,
    // The two years are the dating evidence a metadata-only run leans on.
    attributesToRetrieve: ['objectID', 'referenceId', 'reference', 'model', 'nickname', 'collection', 'releaseYear', 'startYear'],
  });
  return response.hits;
}

/**
 * What {@link getBrandSiblingReferences} found. `indexedInCollection` is how
 * many records the index returned for this brand and collection, the subject
 * included: ZERO means the index knows nothing here — the subject itself is not
 * indexed (unsaved, or a disabled brand whose refs are all evicted) — so the
 * caller must ask Firestore. Non-zero with no siblings is a real answer: the
 * collection is simply too small. `resolveSiblingReferences` acts on it.
 */
export type BrandSiblingReferences = IndexedSiblingReferences;

/**
 * The other references in the subject's COLLECTION, for the AI enrich prompt's
 * Notable Variants ground truth — one Algolia search instead of an unbounded
 * Firestore read of the brand's whole `references` subcollection. That swap
 * replaced ~1,400 Firestore reads PER "Suggest with AI" click with a single
 * search operation (see docs/audits/enrichReference-read-audit-2026-08-05.md,
 * Phase 3 finding #1).
 *
 * Collection-scoped, and ONLY collection-scoped. This used to re-query
 * brand-wide when the collection held fewer than five siblings, on the theory
 * that some list beat none. It did not: the brand-wide list is what let the
 * model present watches from another collection as "variants". A subject with
 * no collection, or in a collection too small, now gets no list, and the prompt
 * omits the section.
 *
 * The SDK's in-memory response cache is deliberately left ON here (unlike the
 * facet-dropdown calls, which bypass it): repeatedly clicking Suggest with AI
 * on one brand is exactly the pattern this fix exists to make cheap, and a
 * sibling list that misses refs added seconds ago is harmless — it is prompt
 * context, not something the admin reads back.
 *
 * Throws if Algolia is unconfigured or the request fails.
 */
export async function getBrandSiblingReferences(
  brandId: string,
  subject: SiblingSubject,
): Promise<BrandSiblingReferences> {
  const collectionName = typeof subject.collection === 'string' ? subject.collection.trim() : '';
  if (!collectionName) {
    // Nothing to search for, and not a question the index needs asking.
    return { siblings: [], indexedInCollection: 0 };
  }
  const hits = await searchSiblingHits(brandId, collectionName);
  const siblings = siblingReferencesFromHits(hits, subject);
  console.info(
    `[siblings] Algolia collection:${collectionName}: ${hits.length} hits → ` +
      `${siblings.length} siblings (min ${MIN_SIBLING_REFERENCES}, cap ${MAX_SIBLING_REFERENCES}), ` +
      '0 Firestore reads',
  );
  return { siblings, indexedInCollection: hits.length };
}

/** One page of the Algolia-backed reference listing. */
export interface ReferenceListResult {
  hits: WatchReferenceAlgoliaHit[];
  /** True match count for the query + filters, independent of the page size. */
  nbHits: number;
  page: number;
  nbPages: number;
  hitsPerPage: number;
}

/**
 * One page of the admin reference listing — search, filter and browse in a
 * single query shape.
 *
 * This is the whole listing: an empty `query` with only a brand filter is the
 * default browse, typed text is the search, and collection/calibre/movementType
 * are facet filters that COMBINE with both. It replaces a paginated
 * `getReferencesForBrandPaginated` fetch that downloaded 50 complete reference
 * documents per page, and it gives the search box Algolia's typo tolerance,
 * prefix matching and accent folding across every searchable attribute rather
 * than only the reference number.
 *
 * Deliberately NOT routed through `looksLikeReference` the way `search()` is:
 * that heuristic sends any query containing a digit to a
 * `restrictSearchableAttributes: ['reference', 'referenceNormalized']` search,
 * so a perfectly ordinary model query like "Datejust 41" matched nothing
 * (verified against the live index: 0 hits restricted, 174 unrestricted). An
 * unrestricted query still puts exact reference matches first — the reference
 * number is a searchable attribute — so nothing is lost by dropping the routing.
 *
 * `sort` selects a virtual sort replica instead of the master index — currently
 * only `createdAt` asc/desc exist (watchlock #860). Omit it for relevance order,
 * which is what a text search wants. Replicas inherit the master's faceting and
 * searchable attributes, so the filters and query shape are identical either
 * way; only the ranking differs.
 *
 * Throws if Algolia is unconfigured or the request fails; callers must fall back
 * to Firestore. A DISABLED brand returns zero hits rather than an error: the
 * indexer evicts every ref of a disabled brand, so empty is not proof the brand
 * is empty.
 */
export async function searchReferences(
  query: string,
  filters: ReferenceListFilters = {},
  page: number = 0,
  hitsPerPage: number = 50,
  sort?: ReferenceSortKey,
): Promise<ReferenceListResult> {
  const target = sort
    ? getClient().initIndex(`${indexName}${REFERENCE_SORT_REPLICAS[sort]}`)
    : getIndex();
  const response = await target.search<WatchReferenceAlgoliaHit>(
    query.trim(),
    buildReferenceListParams(filters, page, hitsPerPage),
  );
  return {
    hits: response.hits,
    nbHits: response.nbHits,
    page: response.page,
    nbPages: response.nbPages,
    hitsPerPage: response.hitsPerPage,
  };
}

/**
 * Fetch specific reference records by their index ids, no query involved.
 *
 * `CuratedCollection.referenceIds[]` stores bare reference ids with no brand
 * alongside them, and references live in the `watchBrands/{brandId}/references`
 * subcollection — so `getReferencesByDocIds`, which needs the brand up front,
 * cannot resolve them, and a Firestore answer would mean a collection-group
 * query per id. Algolia's `getObjects` is an exact id lookup that needs neither.
 *
 * Ids with no record are simply absent from the map (an id can outlive the
 * reference it named, or lead the indexer by a second), so callers must tolerate
 * a short result rather than treating it as a failure. Throws when Algolia is
 * unconfigured or the request fails; callers fall back to showing the raw id.
 */
export async function getReferencesByObjectIds(
  ids: string[],
): Promise<Map<string, WatchReferenceAlgoliaHit>> {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const map = new Map<string, WatchReferenceAlgoliaHit>();
  if (unique.length === 0) return map;

  const response = await getIndex().getObjects<WatchReferenceAlgoliaHit>(
    unique,
    {
      attributesToRetrieve: [
        'objectID',
        'brandId',
        'reference',
        'model',
        'collection',
        'imageUrl',
      ],
    },
  );

  for (const result of response.results) {
    if (result?.objectID) map.set(result.objectID, result);
  }
  return map;
}

/** Filter-dropdown options plus the brand's reference count, in one search. */
export interface BrandListingFacets {
  /**
   * Parents and standalones for this brand. EXACT: the index holds no variants
   * at all (`syncReferenceToAlgolia` deletes any ref that gains a
   * `parentReferenceId`), so there is nothing to subtract.
   */
  count: number;
  collection: BrandFacetValue[];
  calibre: BrandFacetValue[];
  movementType: BrandFacetValue[];
  /**
   * References with no picture, from the `hasImage` facet on the same request —
   * so the counter costs nothing extra and always describes the same brand set
   * as `count`.
   */
  noImageCount: number;
}

/**
 * Every filter dropdown's options AND the brand's parents-only count, from ONE
 * brand-scoped search with `hitsPerPage: 0` — no records downloaded, no
 * Firestore reads.
 *
 * Replaces three separate calls (a count query plus one facet query per
 * dropdown), which in turn had replaced a `getReferencesForBrand(brandId,
 * 10000)` fetch of every full reference document on every brand open.
 *
 * The query is deliberately EMPTY and unfiltered beyond the brand, so the
 * options stay the brand's full vocabulary: driving them off the listing's own
 * response instead would collapse each dropdown to the values surviving the
 * other filters, and picking a collection would leave the collection dropdown
 * showing only that collection.
 *
 * Throws if Algolia is unconfigured or the request fails, and returns zeroes for
 * a disabled (evicted) brand — callers must treat both as "ask Firestore".
 */
export async function getBrandListingFacets(
  brandId: string,
): Promise<BrandListingFacets> {
  const response = await getIndex().search('', {
    facetFilters: [[`brandId:${brandId}`]],
    facets: [...BRAND_LISTING_FACETS],
    maxValuesPerFacet: ALGOLIA_MAX_FACET_VALUES,
    hitsPerPage: 0,
    analytics: false,
    // Bypass the SDK's in-memory response cache. After a bulk enrich writes new
    // collection/calibre values, reopening the brand must offer them as filter
    // options rather than replaying the pre-edit facet response.
    cacheable: false,
  });

  const facets = (response.facets ?? {}) as Record<
    string,
    Record<string, number>
  >;
  return {
    count: response.nbHits,
    collection: foldFacetValues(facets.collection),
    calibre: foldFacetValues(facets.calibre, { foldCase: true }),
    movementType: foldFacetValues(facets.movementType, { foldCase: true }),
    // Algolia reports boolean facets under string keys ("true" / "false").
    noImageCount: facets.hasImage?.false ?? 0,
  };
}

// ── Cross-brand calibre search ────────────────────────────────────────────────
//
// The `watch_references` index has `calibre` as a faceted AND searchable
// attribute (verified against the live index), but NOT `searchable(calibre)` —
// so Algolia's `searchForFacetValues` API is unavailable. Instead we drive the
// calibre picker off a normal query with `facets: ['calibre']`: typing "Elaboré"
// runs a text search, and Algolia returns the calibre facet values present among
// the matching records. This surfaces long-tail values (e.g. "ETA Unitas 6498-1
// Elaboré") that a static top-N facet dump would miss, and needs no new backend.

/** A distinct calibre string and how many references carry it (index-wide). */
export interface CalibreFacetValue {
  /** The exact stored calibre string — use verbatim as a facetFilter value. */
  value: string;
  /** Number of references with this calibre across all brands. */
  count: number;
}

/**
 * Algolia's hard ceiling for `maxValuesPerFacet` (the number of facet values a
 * single search response may return). We request the maximum so the calibre
 * picker sees the COMPLETE set of matching calibre values for a query: the same
 * query text always yields the same candidates, and low-frequency long-tail
 * variants ("SW200-1 Elabora Grade", …) never silently drop out as the data
 * shifts. If a query genuinely matches more than this many distinct calibre
 * values, Algolia returns only the most frequent ones — its documented behaviour;
 * there is no facet-value pagination — and the picker flags that with a hint.
 */
export const ALGOLIA_MAX_FACET_VALUES = 1000;

/**
 * Distinct cross-brand calibre values for the picker.
 *
 * With a `query`, returns the calibre facet values present among records
 * matching that text, filtered to those whose string actually contains the
 * query (so a match on some other searchable field doesn't leak in an unrelated
 * calibre). With an empty `query`, returns the most common calibres index-wide
 * (initial suggestions). `hitsPerPage: 0` — no records are downloaded, only
 * facet counts, so this stays a single cheap call per keystroke.
 */
export async function searchCalibreFacetValues(
  query: string,
  opts?: { maxValues?: number },
): Promise<CalibreFacetValue[]> {
  const trimmed = query.trim();
  // Request the full facet set (up to Algolia's hard cap) so the candidate list
  // is complete and deterministic. Any truncation for display is the caller's
  // concern — this function never slices, so no low-frequency variant is hidden.
  const maxValues = Math.min(
    opts?.maxValues ?? ALGOLIA_MAX_FACET_VALUES,
    ALGOLIA_MAX_FACET_VALUES,
  );
  const response = await getIndex().search(trimmed, {
    hitsPerPage: 0,
    facets: ['calibre'],
    maxValuesPerFacet: maxValues,
    analytics: false,
    // Bypass the JS SDK's in-memory response cache: after James curates refs,
    // re-running the same query text must show fresh facet values, not the
    // stale cached response from before the edits. Cache-bypass is scoped to
    // the cross-brand calibre-search calls; other admin Algolia calls keep the
    // default cache.
    cacheable: false,
  });

  const facet = (response.facets?.calibre ?? {}) as Record<string, number>;
  const folded = foldForMatch(trimmed);
  // Deterministic order: most-carried first, then value ascending as a stable
  // tiebreak so equal-count variants never reshuffle between identical queries.
  return Object.entries(facet)
    .filter(([value]) => (folded ? foldForMatch(value).includes(folded) : true))
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

/**
 * References carrying an exact calibre string, across all brands, paginated.
 * Filters on the `calibre` facet (exact stored value), so pass a `value`
 * returned by searchCalibreFacetValues verbatim.
 */
export async function searchReferencesByCalibre(
  calibre: string,
  opts?: { page?: number; hitsPerPage?: number },
): Promise<AlgoliaSearchResponse<WatchReferenceAlgoliaHit>> {
  const page = opts?.page ?? 0;
  const hitsPerPage = opts?.hitsPerPage ?? 50;
  const response = await getIndex().search<WatchReferenceAlgoliaHit>('', {
    facetFilters: [[`calibre:${calibre}`]],
    hitsPerPage,
    page,
    analytics: false,
    // Bypass the SDK's in-memory cache so a re-search after curating refs
    // reflects the edits rather than the cached pre-edit result set.
    cacheable: false,
    attributesToRetrieve: [
      'objectID',
      'brandId',
      'referenceId',
      'reference',
      'model',
      'collection',
      'productName',
      'calibre',
      'calibreTierId',
      'caseSizeMm',
      'imageUrl',
    ],
  });
  return {
    hits: response.hits,
    nbHits: response.nbHits,
    page: response.page,
    nbPages: response.nbPages,
    hitsPerPage: response.hitsPerPage,
    processingTimeMS: response.processingTimeMS,
    query: response.query,
    facets: response.facets,
  };
}

/**
 * References whose `description` text matches `query`, across all brands —
 * powers the "Also search descriptions" toggle on Cross-brand Calibre Search.
 * A calibre's grade/variant often lives only in the description ("Sellita
 * SW200-1 with Elaboré grade") while the faceted `calibre` field is the bare
 * movement ("Sellita SW200"), so a facet filter alone misses those refs.
 *
 * The search is RESTRICTED to the `description` searchable attribute (verified
 * present on the live index's searchableAttributes as `unordered(description)`),
 * so matches on reference/model/collection/etc. can't leak in — only the
 * description drives a hit. Callers dedup against the exact-calibre set: a hit
 * whose own `calibre` equals the picker value is a calibre match (not a
 * description-only match) and belongs to searchReferencesByCalibre's result set.
 *
 * Cache is bypassed for the same freshness reason as searchReferencesByCalibre.
 */
export async function searchReferencesByDescription(
  query: string,
  opts?: { page?: number; hitsPerPage?: number },
): Promise<AlgoliaSearchResponse<WatchReferenceAlgoliaHit>> {
  const page = opts?.page ?? 0;
  const hitsPerPage = opts?.hitsPerPage ?? 50;
  const response = await getIndex().search<WatchReferenceAlgoliaHit>(query, {
    restrictSearchableAttributes: ['description'],
    hitsPerPage,
    page,
    analytics: false,
    cacheable: false,
    attributesToRetrieve: [
      'objectID',
      'brandId',
      'referenceId',
      'reference',
      'model',
      'collection',
      'productName',
      'calibre',
      'calibreTierId',
      'caseSizeMm',
      'imageUrl',
    ],
  });
  return {
    hits: response.hits,
    nbHits: response.nbHits,
    page: response.page,
    nbPages: response.nbPages,
    hitsPerPage: response.hitsPerPage,
    processingTimeMS: response.processingTimeMS,
    query: response.query,
    facets: response.facets,
  };
}

/**
 * Get brand facets from search results
 */
export interface BrandFacet {
  brandId: string;
  count: number;
}

export function extractBrandFacets(
  response: AlgoliaSearchResponse<WatchReferenceAlgoliaHit>,
): BrandFacet[] {
  if (!response.facets?.brandId) return [];

  return Object.entries(response.facets.brandId)
    .map(([brandId, count]) => ({ brandId, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Get collection facets from search results
 */
export interface CollectionFacet {
  collection: string;
  count: number;
}

export function extractCollectionFacets(
  response: AlgoliaSearchResponse<WatchReferenceAlgoliaHit>,
): CollectionFacet[] {
  if (!response.facets?.collection) return [];

  return Object.entries(response.facets.collection)
    .map(([collection, count]) => ({ collection, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Brands that have at least one indexed reference whose stored `calibre` text is
 * EXACTLY one of `names` — one facet query, no hits fetched.
 *
 * Backs the default scope of the calibre "Reapply to matching references" tool:
 * the brands worth scanning are the ones a text match can come from, and asking
 * the index is far cheaper than reading every brand's references to find out.
 * It is a hint, not the matcher — the index holds parent references of enabled
 * brands only and the comparison is exact-string, so the tool still scans the
 * returned brands in Firestore and applies its own rules.
 *
 * Throws when Algolia is unconfigured or the request fails; the caller treats
 * that as "no extra brands" rather than as an error.
 */
export async function getBrandIdsWithCalibreText(names: readonly string[]): Promise<string[]> {
  const unique = Array.from(new Set(names.map((n) => n.trim()).filter(Boolean)));
  if (unique.length === 0) return [];
  const response = await getIndex().search('', {
    facetFilters: [unique.map((n) => `calibre:${n}`)],
    facets: ['brandId'],
    maxValuesPerFacet: ALGOLIA_MAX_FACET_VALUES,
    hitsPerPage: 0,
    analytics: false,
    cacheable: false,
  });
  const facets = (response.facets ?? {}) as Record<string, Record<string, number>>;
  return Object.keys(facets.brandId ?? {});
}
